import type { GeneratedApplicationProposal } from "@cogo/a2a-gateway/generated-application-proposal";
import { stableStringify } from "@cogo/publisher-core/content-shape";

import {
  ARTIFACT_PERSISTENCE_RECEIPT_SCHEMA_VERSION,
  ARTIFACT_REVISION_ENVELOPE_SCHEMA_VERSION,
  GENERATED_ARTIFACT_APPROVAL_DECISION_SCHEMA_VERSION,
  type ArtifactPersistenceReceipt,
  type ArtifactRevisionRef,
  type CommittedArtifactRevisionEnvelope,
  type GeneratedArtifactTarget,
  type GeneratedArtifactApprovalDecision,
  type UncommittedArtifactRevisionEnvelope,
} from "./artifact-revision-envelope";

/**
 * This discriminator is intentionally specific. Implementations must use the
 * authenticated Studio project persistence plus an independent fresh readback
 * (the existing `persistAndReadBackGeneratedApplication` /
 * `persistGeneratedProjectToServer` seam), not a Zustand/localStorage write.
 */
export const GENERATED_ARTIFACT_PERSISTENCE_PORT_KIND =
  "authenticated-studio-persist-and-read-back" as const;

export interface GeneratedArtifactPersistenceCommand {
  proposal: GeneratedApplicationProposal;
  envelope: UncommittedArtifactRevisionEnvelope;
  approvalDecision: GeneratedArtifactApprovalDecision;
}

export interface GeneratedArtifactPersistencePort {
  readonly kind: typeof GENERATED_ARTIFACT_PERSISTENCE_PORT_KIND;
  persistAndReadBack(
    command: GeneratedArtifactPersistenceCommand,
  ): Promise<unknown>;
}

export const GENERATED_ARTIFACT_COMMIT_ERROR_CODES = {
  APPROVAL_REQUIRED: "APPROVAL_REQUIRED",
  PROPOSAL_ENVELOPE_MISMATCH: "PROPOSAL_ENVELOPE_MISMATCH",
  PERSISTENCE_UNAVAILABLE: "PERSISTENCE_UNAVAILABLE",
  PERSISTENCE_CONFLICT: "PERSISTENCE_CONFLICT",
  PERSISTENCE_READBACK_FAILED: "PERSISTENCE_READBACK_FAILED",
  PERSISTENCE_RECEIPT_INVALID: "PERSISTENCE_RECEIPT_INVALID",
} as const;

export type GeneratedArtifactCommitErrorCode =
  (typeof GENERATED_ARTIFACT_COMMIT_ERROR_CODES)[keyof typeof GENERATED_ARTIFACT_COMMIT_ERROR_CODES];

export class GeneratedArtifactCommitError extends Error {
  readonly code: GeneratedArtifactCommitErrorCode;
  readonly retryable: boolean;
  readonly causeDetail: string | null;

  constructor(input: {
    code: GeneratedArtifactCommitErrorCode;
    message: string;
    retryable: boolean;
    causeDetail?: string | null;
  }) {
    super(input.message);
    this.name = "GeneratedArtifactCommitError";
    this.code = input.code;
    this.retryable = input.retryable;
    this.causeDetail = input.causeDetail ?? null;
  }
}

export type CommitGeneratedArtifactResult =
  | {
      status: "committed";
      envelope: CommittedArtifactRevisionEnvelope;
      receipt: ArtifactPersistenceReceipt;
    }
  | {
      status: "not-committed";
      error: GeneratedArtifactCommitError;
    };

export interface CommitGeneratedArtifactInput {
  proposal: GeneratedApplicationProposal;
  envelope: UncommittedArtifactRevisionEnvelope;
  decision: GeneratedArtifactApprovalDecision;
  persistence: GeneratedArtifactPersistencePort;
}

function notCommitted(
  input: ConstructorParameters<typeof GeneratedArtifactCommitError>[0],
): CommitGeneratedArtifactResult {
  return {
    status: "not-committed",
    error: new GeneratedArtifactCommitError(input),
  };
}

async function sha256Content(value: unknown): Promise<`sha256:${string}`> {
  const bytes = new TextEncoder().encode(stableStringify(value));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return `sha256:${Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("")}`;
}

async function proposalMatchesEnvelope(
  proposal: GeneratedApplicationProposal,
  envelope: UncommittedArtifactRevisionEnvelope,
): Promise<boolean> {
  const actualContentHash = await sha256Content(proposal.surface.cogoJson);
  return (
    envelope.schemaVersion === ARTIFACT_REVISION_ENVELOPE_SCHEMA_VERSION &&
    envelope.artifactId === proposal.proposalId &&
    envelope.runId === proposal.requestedBy.runId &&
    envelope.requestId.length > 0 &&
    envelope.revision.authority.length > 0 &&
    envelope.revision.value.length > 0 &&
    /^sha256:[a-f0-9]{64}$/.test(envelope.contentHash) &&
    envelope.contentHash === actualContentHash &&
    envelope.parentRevision !== null &&
    envelope.parentRevision.authority.trim().length > 0 &&
    envelope.parentRevision.value === proposal.surface.baseRevision &&
    envelope.persistenceReceipt === null
  );
}

function sameRevision(
  left: UncommittedArtifactRevisionEnvelope["revision"] | null,
  right: UncommittedArtifactRevisionEnvelope["revision"] | null,
): boolean {
  if (left === null || right === null) return left === right;
  return left.authority === right.authority && left.value === right.value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function validRevision(revision: unknown): revision is ArtifactRevisionRef {
  return (
    isRecord(revision) &&
    typeof revision.authority === "string" &&
    revision.authority.trim().length > 0 &&
    typeof revision.value === "string" &&
    revision.value.trim().length > 0
  );
}

function validTarget(target: unknown): target is GeneratedArtifactTarget {
  if (!isRecord(target)) return false;
  if (target.kind === "existing-project") {
    return typeof target.projectId === "string" && target.projectId.trim().length > 0;
  }
  if (target.kind === "existing-bot-project") {
    return (
      typeof target.projectId === "string" &&
      target.projectId.trim().length > 0 &&
      typeof target.ownerBotProfileId === "string" &&
      target.ownerBotProfileId.trim().length > 0
    );
  }
  return (
    target.kind === "new-project" &&
    typeof target.ownerBotProfileId === "string" &&
    target.ownerBotProfileId.trim().length > 0 &&
    typeof target.suggestedName === "string" &&
    target.suggestedName.trim().length > 0
  );
}

function approvalMatchesEnvelope(
  decision: unknown,
  envelope: UncommittedArtifactRevisionEnvelope,
): decision is GeneratedArtifactApprovalDecision {
  if (
    !isRecord(decision) ||
    typeof decision.decisionId !== "string" ||
    typeof decision.artifactId !== "string" ||
    typeof decision.requestId !== "string" ||
    typeof decision.runId !== "string" ||
    typeof decision.actorId !== "string" ||
    decision.actorKind !== "human" ||
    (decision.decision !== "approved" && decision.decision !== "rejected") ||
    !validTarget(decision.target) ||
    !validRevision(decision.revision) ||
    (decision.parentRevision !== null && !validRevision(decision.parentRevision)) ||
    typeof decision.contentHash !== "string" ||
    !/^sha256:[a-f0-9]{64}$/.test(decision.contentHash)
  ) {
    return false;
  }
  return (
    decision.schemaVersion === GENERATED_ARTIFACT_APPROVAL_DECISION_SCHEMA_VERSION &&
    decision.decisionId.trim().length > 0 &&
    decision.actorId.trim().length > 0 &&
    decision.artifactId === envelope.artifactId &&
    decision.requestId === envelope.requestId &&
    decision.runId === envelope.runId &&
    decision.contentHash === envelope.contentHash &&
    sameTarget(decision.target, envelope.target) &&
    sameRevision(decision.revision, envelope.revision) &&
    sameRevision(decision.parentRevision, envelope.parentRevision)
  );
}

function sameTarget(
  left: UncommittedArtifactRevisionEnvelope["target"],
  right: UncommittedArtifactRevisionEnvelope["target"],
): boolean {
  if (left.kind !== right.kind) return false;
  if (left.kind === "existing-project" && right.kind === "existing-project") {
    return left.projectId === right.projectId;
  }
  if (
    left.kind === "existing-bot-project" &&
    right.kind === "existing-bot-project"
  ) {
    return (
      left.projectId === right.projectId &&
      left.ownerBotProfileId === right.ownerBotProfileId
    );
  }
  return (
    left.kind === "new-project" &&
    right.kind === "new-project" &&
    left.ownerBotProfileId === right.ownerBotProfileId &&
    left.suggestedName === right.suggestedName
  );
}

function contentRefKey(ref: { ref: string; contentHash: string }): string {
  return `${ref.ref}\u0000${ref.contentHash}`;
}

function validContentRef(
  ref: unknown,
): ref is { ref: string; contentHash: `sha256:${string}` } {
  return (
    isRecord(ref) &&
    typeof ref.ref === "string" &&
    ref.ref.length > 0 &&
    typeof ref.contentHash === "string" &&
    /^sha256:[a-f0-9]{64}$/.test(ref.contentHash)
  );
}

function validContentRefs(
  refs: unknown,
): refs is readonly { ref: string; contentHash: `sha256:${string}` }[] {
  return Array.isArray(refs) && refs.every(validContentRef);
}

function containsEvidence(
  observed: readonly { ref: string; contentHash: string }[],
  required: readonly { ref: string; contentHash: string }[],
): boolean {
  const observedKeys = new Set(observed.map(contentRefKey));
  return required.every((ref) => validContentRef(ref) && observedKeys.has(contentRefKey(ref)));
}

function receiptMatchesCommit(
  committed: unknown,
  command: GeneratedArtifactPersistenceCommand,
): committed is CommittedArtifactRevisionEnvelope {
  if (!isRecord(committed) || !isRecord(committed.persistenceReceipt)) return false;
  const receipt = committed.persistenceReceipt;
  if (
    !validTarget(committed.target) ||
    !validRevision(committed.revision) ||
    (committed.parentRevision !== null && !validRevision(committed.parentRevision)) ||
    !validContentRefs(committed.evidenceRefs) ||
    !validRevision(receipt.revision) ||
    !validContentRef(receipt.persistenceWriteRef) ||
    !validContentRef(receipt.readbackRef) ||
    !validContentRefs(receipt.evidenceRefs) ||
    typeof receipt.receiptId !== "string" ||
    typeof receipt.projectId !== "string" ||
    typeof receipt.approvalDecisionId !== "string" ||
    typeof receipt.approvalActorId !== "string" ||
    receipt.approvalActorKind !== "human" ||
    (typeof receipt.ownerBotProfileId !== "string" && receipt.ownerBotProfileId !== null) ||
    typeof receipt.persistedAt !== "string"
  ) {
    return false;
  }
  const targetProjectId = command.envelope.target.kind === "new-project"
    ? receipt.projectId
    : command.envelope.target.projectId;
  const targetOwnerBotProfileId =
    command.envelope.target.kind === "existing-project"
      ? null
      : command.envelope.target.ownerBotProfileId;

  return (
    committed.schemaVersion === ARTIFACT_REVISION_ENVELOPE_SCHEMA_VERSION &&
    committed.artifactId === command.envelope.artifactId &&
    committed.requestId === command.envelope.requestId &&
    committed.runId === command.envelope.runId &&
    committed.contentHash === command.envelope.contentHash &&
    validRevision(committed.revision) &&
    sameTarget(committed.target, command.envelope.target) &&
    sameRevision(committed.parentRevision, command.envelope.parentRevision) &&
    containsEvidence(committed.evidenceRefs, command.envelope.evidenceRefs) &&
    receipt.schemaVersion === ARTIFACT_PERSISTENCE_RECEIPT_SCHEMA_VERSION &&
    receipt.receiptId.length > 0 &&
    receipt.artifactId === committed.artifactId &&
    receipt.requestId === committed.requestId &&
    receipt.runId === committed.runId &&
    receipt.approvalDecisionId === command.approvalDecision.decisionId &&
    receipt.approvalActorId === command.approvalDecision.actorId &&
    receipt.approvalActorKind === command.approvalDecision.actorKind &&
    receipt.projectId.trim().length > 0 &&
    receipt.projectId === targetProjectId &&
    receipt.ownerBotProfileId === targetOwnerBotProfileId &&
    receipt.contentHash === committed.contentHash &&
    validRevision(receipt.revision) &&
    sameRevision(receipt.revision, committed.revision) &&
    validContentRef(receipt.persistenceWriteRef) &&
    receipt.persistenceWriteRef.contentHash === committed.contentHash &&
    validContentRef(receipt.readbackRef) &&
    receipt.readbackRef.contentHash === committed.contentHash &&
    receipt.persistenceWriteRef.ref !== receipt.readbackRef.ref &&
    containsEvidence(receipt.evidenceRefs, committed.evidenceRefs) &&
    receipt.persistedAt.length > 0
  );
}

/**
 * Approval-gated shared commit adapter for Chat and Bot generated artifacts.
 *
 * This function never writes Studio state. It either refuses locally or hands
 * the unchanged proposal/envelope pair to the authenticated persistence port,
 * then accepts only a receipt bound to that exact artifact, request, run,
 * content hash, target, and resulting revision.
 */
export async function commitGeneratedArtifact(
  input: CommitGeneratedArtifactInput,
): Promise<CommitGeneratedArtifactResult> {
  if (!approvalMatchesEnvelope(input.decision, input.envelope)) {
    return notCommitted({
      code: GENERATED_ARTIFACT_COMMIT_ERROR_CODES.PROPOSAL_ENVELOPE_MISMATCH,
      message: "The approval decision does not match the generated revision envelope.",
      retryable: false,
    });
  }
  if (input.decision.decision !== "approved") {
    return notCommitted({
      code: GENERATED_ARTIFACT_COMMIT_ERROR_CODES.APPROVAL_REQUIRED,
      message: "Generated artifact persistence requires explicit approval.",
      retryable: false,
    });
  }
  if (!(await proposalMatchesEnvelope(input.proposal, input.envelope))) {
    return notCommitted({
      code: GENERATED_ARTIFACT_COMMIT_ERROR_CODES.PROPOSAL_ENVELOPE_MISMATCH,
      message: "The generated proposal does not match its revision envelope.",
      retryable: false,
    });
  }
  if (input.persistence.kind !== GENERATED_ARTIFACT_PERSISTENCE_PORT_KIND) {
    return notCommitted({
      code: GENERATED_ARTIFACT_COMMIT_ERROR_CODES.PERSISTENCE_UNAVAILABLE,
      message: "Authenticated generated artifact persistence is unavailable.",
      retryable: true,
    });
  }

  const command: GeneratedArtifactPersistenceCommand = {
    proposal: input.proposal,
    envelope: input.envelope,
    approvalDecision: input.decision,
  };
  let committed: unknown;
  try {
    committed = await input.persistence.persistAndReadBack(command);
  } catch (error) {
    if (
      isRecord(error) &&
      error.code === "GENERATED_ARTIFACT_PROJECT_CONFLICT"
    ) {
      return notCommitted({
        code: GENERATED_ARTIFACT_COMMIT_ERROR_CODES.PERSISTENCE_CONFLICT,
        message: "The target project changed before artifact persistence.",
        retryable: false,
        causeDetail: error instanceof Error ? error.message : String(error),
      });
    }
    return notCommitted({
      code: GENERATED_ARTIFACT_COMMIT_ERROR_CODES.PERSISTENCE_READBACK_FAILED,
      message: "Generated artifact persistence or authenticated readback failed.",
      retryable: true,
      causeDetail: error instanceof Error ? error.message : String(error),
    });
  }

  if (!receiptMatchesCommit(committed, command)) {
    return notCommitted({
      code: GENERATED_ARTIFACT_COMMIT_ERROR_CODES.PERSISTENCE_RECEIPT_INVALID,
      message: "Generated artifact persistence returned an unbound receipt.",
      retryable: false,
    });
  }

  return {
    status: "committed",
    envelope: committed,
    receipt: committed.persistenceReceipt,
  };
}
